import { createSelector } from 'reselect';

import type { MayBe } from 'types/common';
import { getI18nMessage } from 'utils/utils';
import { getItemsWithSeverities } from 'works/utils';
import { QUESTION_TYPE } from 'modules/entity/subEntities/audit/constants';
import { getUnitForValue } from 'constants/UnitConstants';
import { CONTROL_REPORT_ALLOWED_TYPES } from 'constants/Global';
import { CHECKLIST_TYPE, SEVERITY } from 'modules/entity/constants';
import { getDefaultLanguage, getEntity, getLanguage, getSmallDistanceUnit } from 'modules/entity/selectors';
import type {
  CheckboxResponse, DateTimeResponse, ExplodedViewResponse, NumberResponse, PadsThicknessResponse,
  RadioButtonResponse, ResponseSection, ResponseTypes, SeverityChoiceEnum, TextboxResponse, ThicknessResponse, TireReportResponse,
  YesNoResponse,
} from 'modules/entity/subEntities/audit/types';

import { getUI } from '.';
import { getAudits, getResponseById } from './audits';

const getId = (_state: unknown, props: string) => props;
const getProps = (_state: unknown, _props: unknown, props: unknown) => props;

/**
 * The various `*Response` shapes only share their common fields via a plain union (no literal
 * discriminant on `type` to narrow from) — this envelope pulls each field's real type from its
 * actual variant, matching how this runtime code reads across the whole response family.
 */
type ResponseEnvelope = {
  type: ResponseTypes['type'];
  response?: CheckboxResponse['response'] | TextboxResponse['response'];
  choice?: RadioButtonResponse['choice'] | YesNoResponse['choice'];
  date?: DateTimeResponse['date'];
  time?: DateTimeResponse['time'];
  markers?: ExplodedViewResponse['markers'];
  severity?: SeverityChoiceEnum;
  tires?: TireReportResponse['tires'];
  measuredThickness?: PadsThicknessResponse['measuredThickness'] | ThicknessResponse['measuredThickness'];
  number?: NumberResponse['number'];
  // No literal discriminant exists across the `*Response` family (see modules/entity/subEntities/audit/types.js) to
  // narrow `question` per-variant safely — kept `unknown` and narrowed locally where read (RADIO_BUTTON case below).
  question?: unknown;
};

const isQuestionDisplayed = (
  { response = [], choice, date, markers = [], severity, time, type, tires = [] }: ResponseEnvelope,
  hasFilter: boolean
): boolean => {
  switch (type) {
    case QUESTION_TYPE.CHECKBOX:
      return response.length > 0;
    case QUESTION_TYPE.EXPLODED_VIEW:
      return markers.length > 0;
    case QUESTION_TYPE.DATE_TIME:
      return Boolean((date || time) && !hasFilter);
    case QUESTION_TYPE.RADIO_BUTTON:
      return Boolean(choice) && !hasFilter;
    case QUESTION_TYPE.TIRE_REPORT:
      return tires.length > 0;
    default:
      return Boolean(severity && (!hasFilter || severity === SEVERITY.LOW || severity === SEVERITY.HIGH));
  }
};

const getControlReportUI = createSelector(getUI, (ui) => ui.controlReport);

export const hasFiltered = createSelector(getControlReportUI, (controlReport) => controlReport.hasFiltered);

export const getControlReportOkWorks = createSelector(getEntity, ({ checklists = [] }) =>
  checklists.reduce<Array<ReturnType<typeof getItemsWithSeverities>[number]>>((acc, checklist) => {
    const { type, originAudit, originTireScanId } = checklist;
    const isRightType = type === CHECKLIST_TYPE.WORK || type === CHECKLIST_TYPE.REMINDER_SCHEDULED;
    const canAdd = !originAudit && !originTireScanId && isRightType;

    if (canAdd) {
      acc.push(...getItemsWithSeverities(checklist, [SEVERITY.NONE]));
    }
    return acc;
  }, []));

export const getControlReportSections = createSelector(getAudits, (audits) =>
  audits.flatMap(({ sections = [] }) => sections.filter(({ hide }) => !hide)));

export const getControlReportTireScans = createSelector(getEntity, ({ checklists = [] }) =>
  checklists.filter(({ originTireScanId }) => originTireScanId));

const getSectionById = createSelector(getControlReportSections, getId, (sections, id) =>
  sections.find(({ id: sectionId }) => id === sectionId));

const getQuestions = (questions: Array<ResponseTypes>, hasFilter: boolean) =>
  questions.filter(
    (question) => CONTROL_REPORT_ALLOWED_TYPES.includes(question.type) && isQuestionDisplayed(question, hasFilter)
  );

const getAllDisplayedQuestions = createSelector(getControlReportSections, hasFiltered, (sections, hasFilter) =>
  getQuestions(
    sections.flatMap(({ questions }) => questions),
    hasFilter
  ));

export const getDisplayedQuestions = createSelector(
  getSectionById,
  hasFiltered,
  (section: MayBe<ResponseSection>, hasFilter) => getQuestions(section?.questions ?? [], hasFilter)
);

export const getAnswer = createSelector(
  getResponseById,
  getProps,
  getLanguage,
  getDefaultLanguage,
  getSmallDistanceUnit,
  (response: ResponseEnvelope, intl, language, dealerLanguage, unit) => {
    switch (response.type) {
      case QUESTION_TYPE.DATE_TIME:
        return `${response.date ? `${intl.formatDate(response.date)}` : ''}${response.time || ''}`;
      case QUESTION_TYPE.RADIO_BUTTON: {
        const radioButtonQuestion = response.question as MayBe<RadioButtonResponse['question']>;
        return getI18nMessage(
          language,
          dealerLanguage,
          radioButtonQuestion?.choices?.find((currentChoice) => currentChoice.id === response.choice)?.labels
        );
      }
      case QUESTION_TYPE.PADS_THICKNESS:
      case QUESTION_TYPE.THICKNESS:
        return response.measuredThickness ? getUnitForValue(response.measuredThickness, unit, intl) : undefined;
      case QUESTION_TYPE.NUMBER:
        return response.number;
      default:
        return undefined;
    }
  }
);

const hasControlReportOkWorks = createSelector(getControlReportOkWorks, (okWorks) => okWorks.length > 0);

const hasControlReportTireScans = createSelector(getControlReportTireScans, (tireScans) => tireScans.length > 0);

const hasControlReportSections = createSelector(getControlReportSections, (sections) => sections.length > 0);

export const hasControlReport = createSelector(
  hasControlReportOkWorks,
  hasControlReportSections,
  hasControlReportTireScans,
  (...args) => args.some(Boolean)
);

type PointsTotal = { ok: number; recommended: number; urgent: number };

const DEFAULT_POINTS: PointsTotal = { ok: 0, recommended: 0, urgent: 0 };

const getAdvicesPoints = <T extends { severity?: string | null }>(advices: Array<T>, hasFilter: boolean): PointsTotal =>
  advices.reduce<PointsTotal>(
    (acc, { severity }) => {
      if (severity === SEVERITY.HIGH) {
        acc.urgent++;
      } else if (severity === SEVERITY.LOW) {
        acc.recommended++;
      } else if (!hasFilter) {
        acc.ok++;
      }
      return acc;
    },
    { ok: 0, recommended: 0, urgent: 0 }
  );

const getTireScansPoints = createSelector(getControlReportTireScans, hasFiltered, (tireScans, hasFilter) => {
  if (tireScans.length > 0) {
    const tireAdvices = tireScans.flatMap(({ items = [] }) => items.flatMap(({ advices = [] }) => advices));
    return getAdvicesPoints(tireAdvices, hasFilter);
  }
  return DEFAULT_POINTS;
});

const getResponsePoints = (
  { response = [], choice, date, time, markers = [], severity, type, tires = [] }: ResponseEnvelope,
  hasFilter: boolean
): PointsTotal => {
  switch (type) {
    case QUESTION_TYPE.CHECKBOX:
      return { ...DEFAULT_POINTS, urgent: response.length ?? 0 };
    case QUESTION_TYPE.RADIO_BUTTON:
      return { ...DEFAULT_POINTS, ok: choice ? 1 : 0 };
    case QUESTION_TYPE.DATE_TIME:
      return { ...DEFAULT_POINTS, ok: date || time ? 1 : 0 };
    case QUESTION_TYPE.EXPLODED_VIEW:
      return getAdvicesPoints(markers, hasFilter);
    case QUESTION_TYPE.TIRE_REPORT:
      return getAdvicesPoints(tires, hasFilter);
    default:
      return {
        recommended: severity === SEVERITY.LOW ? 1 : 0,
        urgent: severity === SEVERITY.HIGH ? 1 : 0,
        ok: severity && severity !== SEVERITY.HIGH && severity !== SEVERITY.LOW ? 1 : 0,
      };
  }
};

const getAuditPoints = createSelector(
  getAllDisplayedQuestions,
  hasFiltered,
  (questions, hasFilter) => (
    questions.reduce<PointsTotal>((acc, question) => {
      const points = getResponsePoints(question, hasFilter);
      return {
        ok: acc.ok + points.ok,
        recommended: acc.recommended + points.recommended,
        urgent: acc.urgent + points.urgent,
      };
    }, DEFAULT_POINTS)
  )
);

const getOkWorksPoints = createSelector(
  getControlReportOkWorks,
  hasFiltered,
  (works, hasFilter) => {
    if (hasFilter) {
      return 0;
    }
    const advices = works.flatMap(({ advices: workAdvices }) => workAdvices ?? []);
    return advices.filter(({ severity }) => severity === SEVERITY.NONE).length;
  }
);

export const getControlReportOkPoints = createSelector(
  getTireScansPoints,
  getAuditPoints,
  getOkWorksPoints,
  (tireScansPoints, auditPoints, okWorksPoints) => tireScansPoints.ok + auditPoints.ok + okWorksPoints
);

export const getControlReportRecommendedPoints = createSelector(
  getTireScansPoints,
  getAuditPoints,
  (tireScansPoints, auditPoints) => tireScansPoints.recommended + auditPoints.recommended
);

export const getControlReportUrgentPoints = createSelector(
  getTireScansPoints,
  getAuditPoints,
  (tireScansPoints, auditPoints) => tireScansPoints.urgent + auditPoints.urgent
);

export const getControlReportRecommendedAndUrgentPoints = createSelector(
  getControlReportRecommendedPoints,
  getControlReportUrgentPoints,
  (recommended, urgent) => recommended + urgent
);

export const getControlReportTotalPoints = createSelector(
  getControlReportOkPoints,
  getControlReportRecommendedAndUrgentPoints,
  (ok, urgentAndRecommended) => ok + urgentAndRecommended
);

export const hasUrgentOrRecommendedPoints = createSelector(
  getControlReportRecommendedAndUrgentPoints,
  (points) => points > 0
);
